import styled from "@emotion/styled";

import { Button, Tooltip, classNames } from "@skilitics/designer-ui-components";
import { offsetOrigins, type OffsetOrigin } from "@skilitics/drag-drop";
import { useFormatMessage, type FormatStrings } from "@skilitics/localization";

const GRID_BUTTON_SIZE = 16;
const GRID_GAP_SIZE = 2;

const OffsetOriginGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, ${GRID_BUTTON_SIZE}px);
  grid-template-rows: repeat(3, ${GRID_BUTTON_SIZE}px);
  gap: ${GRID_GAP_SIZE}px;
  width: ${GRID_BUTTON_SIZE * 3 + GRID_GAP_SIZE * 2}px;
  height: ${GRID_BUTTON_SIZE * 3 + GRID_GAP_SIZE * 2}px;
  border: solid 1px ${({ theme }) => theme.outlineSoft};
  box-sizing: content-box;
  background: ${({ theme }) => theme.surfaceVariant};
  padding: 2px;
`;

const GridButton = styled(Button)`
  height: 100%;
  width: 100%;
  min-width: 0;
  background: ${({ theme }) => theme.surface};
  border-radius: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &[disabled] {
    background: ${({ theme }) => theme.surfaceVariant};
  }

  &:hover .dot {
    background: ${({ theme }) => theme.primary};
    opacity: 0.5;
  }
  &.active .dot {
    background: ${({ theme }) => theme.primary};
    opacity: 1;
    height: 6px;
    width: 6px;
  }
`;

const Dot = styled.span`
  height: 3px;
  width: 3px;
  border-radius: 50%;
  background: ${({ theme }) => theme.outlineSoft};
`;

type Props = {
  activeOffsetOrigin?: OffsetOrigin;
  onOffsetOriginChange: (offsetOrigin: OffsetOrigin) => void;
  disabled?: boolean;
  disabledOffsetOrigins?: ReadonlySet<OffsetOrigin>;
  disabledOffsetOriginTooltip?: string;
};

export const OffsetOriginGrid = ({
  activeOffsetOrigin = "topLeft",
  onOffsetOriginChange,
  disabled,
  disabledOffsetOrigins,
  disabledOffsetOriginTooltip,
}: Props) => {
  const formatMessage = useFormatMessage(offsetOriginFormatStrings);
  return (
    <OffsetOriginGridContainer>
      {offsetOrigins.map((offsetOrigin) => {
        const isActive = activeOffsetOrigin === offsetOrigin;
        const isDisabledBySlider = !!disabledOffsetOrigins?.has(offsetOrigin);
        const tooltipTitle = disabled
          ? undefined
          : isDisabledBySlider && disabledOffsetOriginTooltip
            ? disabledOffsetOriginTooltip
            : formatMessage(offsetOrigin);
        return (
          <Tooltip
            key={offsetOrigin}
            title={tooltipTitle}
            placement={offsetOrigin.startsWith("bottom") ? "bottom" : "top"}
            disableFocusListener
          >
            <span>
              <GridButton
                value={offsetOrigin}
                size="small"
                onClick={() => onOffsetOriginChange(offsetOrigin)}
                className={classNames({ active: isActive })}
                disabled={disabled || disabledOffsetOrigins?.has(offsetOrigin)}
              >
                <Dot className="dot" />
              </GridButton>
            </span>
          </Tooltip>
        );
      })}
    </OffsetOriginGridContainer>
  );
};

const offsetOriginFormatStrings: Record<OffsetOrigin, FormatStrings> = {
  topLeft: { en: "Offset from Top Left" },
  topCenter: { en: "Offset from Top Center" },
  topRight: { en: "Offset from Top Right" },
  centerLeft: { en: "Offset from Center Left" },
  center: { en: "Offset from Center" },
  centerRight: { en: "Offset from Center Right" },
  bottomLeft: { en: "Offset from Bottom Left" },
  bottomCenter: { en: "Offset from Bottom Center" },
  bottomRight: { en: "Offset from Bottom Right" },
};
